import { useCallback, useEffect, useMemo, useState } from 'react';

export interface SelectionItem<T = unknown> {
  /** Unique identifier for the item */
  id: string;
  /** The underlying data for this item */
  data?: T;
  /** Whether this item is disabled */
  disabled?: boolean;
}

export interface UseSelectionGroupOptions<T = unknown> {
  /** Array of badge items */
  items: SelectionItem<T>[];
  /** Initial selected item IDs (for uncontrolled mode) */
  initialSelectedIds?: string[];
  /** Selected item IDs (for controlled mode) */
  selectedIds?: string[];
  /** Selection mode - single or multi-select */
  multiselect?: boolean;
  /** Callback when selection changes */
  onSelectionChange?: (selectedItems: SelectionItem<T>[]) => void;
  /** Whether the entire group is disabled */
  disabled?: boolean;
}

export interface UseSelectionGroupReturn<T = unknown> {
  /** Currently selected items */
  selectedItems: SelectionItem<T>[];
  /** Currently selected item IDs */
  selectedIds: string[];
  /** Check if an item is selected */
  isSelected: (id: string) => boolean;
  /** Toggle selection of an item */
  toggleSelection: (item: SelectionItem<T>) => void;
  /** Set selection directly */
  setSelection: (ids: string[]) => void;
  /** Clear all selections */
  clearSelection: () => void;
}

/**
 * Headless hook for managing badge selection state and behavior
 * Provides all the logic without any UI rendering
 * Supports both controlled and uncontrolled modes
 *
 * @example Uncontrolled mode
 * ```tsx
 * const { selectedItems, isSelected, toggleSelection } = useSelectionGroup({
 *   items: badges,
 *   initialSelectedIds: ['badge1'],
 *   multiselect: true,
 *   onSelectionChange: (items) => console.log('Selected:', items)
 * });
 * ```
 *
 * @example Controlled mode
 * ```tsx
 * const { isSelected, toggleSelection } = useSelectionGroup({
 *   items: badges,
 *   selectedIds: externalSelectedIds,
 *   multiselect: true,
 *   onSelectionChange: (items) => setExternalSelectedIds(items.map(i => i.id))
 * });
 * ```
 */
export const useSelectionGroup = <T = unknown>({
  items,
  initialSelectedIds = [],
  selectedIds: controlledSelectedIds,
  multiselect = false,
  onSelectionChange,
  disabled = false,
}: UseSelectionGroupOptions<T>): UseSelectionGroupReturn<T> => {
  const [internalSelectedIds, setInternalSelectedIds] = useState<string[]>(initialSelectedIds);

  // Update internal state when initialSelectedIds changes (for uncontrolled mode only)
  useEffect(() => {
    setInternalSelectedIds(initialSelectedIds);
  }, [JSON.stringify(initialSelectedIds)]);

  // Determine if component is controlled or uncontrolled
  const isControlled = controlledSelectedIds !== undefined;

  // Use controlled or uncontrolled selected IDs
  const selectedIds = isControlled ? controlledSelectedIds : internalSelectedIds;
  const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Get the currently selected items
  const selectedItems = useMemo(
    () => items.filter((item) => selectedIdsSet.has(item.id)),
    [items, selectedIdsSet]
  );

  // Check if an item is selected
  const isSelected = useCallback((id: string) => selectedIdsSet.has(id), [selectedIdsSet]);

  // Toggle selection of an item
  const toggleSelection = useCallback(
    (item: SelectionItem<T>) => {
      if (disabled || item.disabled) return;

      let newSelectedIds: string[];

      if (multiselect) {
        if (selectedIdsSet.has(item.id)) {
          newSelectedIds = selectedIds.filter((id) => id !== item.id);
        } else {
          newSelectedIds = [...selectedIds, item.id];
        }
      } else {
        newSelectedIds = selectedIdsSet.has(item.id) ? [] : [item.id];
      }

      // Only update internal state if uncontrolled
      if (!isControlled) {
        setInternalSelectedIds(newSelectedIds);
      }

      // Always notify parent component of the change
      const newSelectedItems = items.filter((i) => newSelectedIds.includes(i.id));
      onSelectionChange?.(newSelectedItems);
    },
    [disabled, multiselect, selectedIds, selectedIdsSet, items, onSelectionChange, isControlled]
  );

  // Set selection directly
  const setSelection = useCallback(
    (ids: string[]) => {
      // Only update internal state if uncontrolled
      if (!isControlled) {
        setInternalSelectedIds(ids);
      }
      const newSelectedItems = items.filter((item) => ids.includes(item.id));
      onSelectionChange?.(newSelectedItems);
    },
    [items, onSelectionChange, isControlled]
  );

  // Clear all selections
  const clearSelection = useCallback(() => {
    // Only update internal state if uncontrolled
    if (!isControlled) {
      setInternalSelectedIds([]);
    }
    onSelectionChange?.([]);
  }, [onSelectionChange, isControlled]);

  return {
    selectedItems,
    selectedIds,
    isSelected,
    toggleSelection,
    setSelection,
    clearSelection,
  };
};
